import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Brain, TrendingUp, Heart, AlertTriangle, Lightbulb, Target, ArrowRight, Loader2, Download } from 'lucide-react';
import { AnalysisResults } from '../../types/notes';
import { exportToJTBD } from '@/services/export';

interface MobileAnalysisPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isAnalyzing: boolean;
  results: AnalysisResults | null;
  isMultiInterview?: boolean;
  interviewNames?: string[];
}

export const MobileAnalysisPanel = ({ isOpen, onClose, isAnalyzing, results, isMultiInterview = false, interviewNames = [] }: MobileAnalysisPanelProps) => {
  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-4">
          <DrawerTitle className="text-center flex items-center justify-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            {isMultiInterview ? 'Cross-Interview Analysis' : 'AI Analysis'}
          </DrawerTitle>
          <DrawerDescription className="text-center">
            {isMultiInterview 
              ? `Patterns and insights across ${interviewNames.length} interviews`
              : 'Insights and patterns from your interview notes'
            }
          </DrawerDescription>
          {results && (
            <div className="flex justify-center mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportToJTBD(results, isMultiInterview, interviewNames)}
                className="flex items-center space-x-1"
              >
                <Download className="w-3 h-3" />
                <span>Export JTBD</span>
              </Button>
            </div>
          )}
        </DrawerHeader>
        <div className="px-6 pb-6 max-h-[70vh] overflow-y-auto">
          {isAnalyzing ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="relative">
                <Brain className="w-12 h-12 text-primary animate-pulse-soft" />
                <Loader2 className="w-6 h-6 text-primary animate-spin absolute -top-1 -right-1" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="font-medium text-gray-900">Analyzing Interview Data</h3>
                <p className="text-sm text-gray-600">
                  AI is processing your notes to identify patterns and insights...
                </p>
              </div>
              <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
                <div className="bg-primary h-2 rounded-full animate-pulse w-2/3"></div>
              </div>
            </div>
          ) : results ? (
            <div className="space-y-6">
              <MobileAnalysisSection
                title="Key Themes"
                icon={TrendingUp}
                items={results.themes}
                color="text-primary"
              />
              
              <MobileAnalysisSection
                title="Emotional Patterns"
                icon={Heart}
                items={results.emotions}
                color="text-pink-600"
              />
              
              <MobileAnalysisSection
                title="Pain Points"
                icon={AlertTriangle}
                items={results.painPoints}
                color="text-red-600"
              />
              
              <MobileAnalysisSection
                title="Key Insights"
                icon={Lightbulb}
                items={results.insights}
                color="text-green-600"
              />
              
              <MobileAnalysisSection
                title="Opportunities"
                icon={Target}
                items={results.opportunities || []}
                color="text-purple-600"
              />
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <ArrowRight className="w-4 h-4 text-secondary" />
                  <h3 className="font-medium text-gray-900">Recommendations</h3>
                </div>
                <Card className="p-4 bg-secondary/5 border-secondary/20">
                  <ul className="space-y-2">
                    {results.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <div className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 space-y-4">
              <Brain className="w-12 h-12 text-gray-400 mx-auto" />
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900">No Analysis Yet</h3>
                <p className="text-sm text-gray-600">
                  Add some notes to your canvas and click "Analyze Interview" to get AI-powered insights.
                </p>
              </div>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

interface MobileAnalysisSectionProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: string[];
  color: string;
}

const MobileAnalysisSection = ({ title, icon: Icon, items, color }: MobileAnalysisSectionProps) => (
  <div className="space-y-3">
    <div className="flex items-center space-x-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <h3 className="font-medium text-gray-900">{title}</h3>
      <Badge variant="outline" className="text-xs">
        {items.length}
      </Badge>
    </div>
    <div className="space-y-2">
      {items.map((item, index) => (
        <Card key={index} className="p-3 border-l-4 border-l-gray-200 hover:border-l-primary transition-colors">
          <p className="text-sm text-gray-700">{item}</p>
        </Card>
      ))}
    </div>
  </div>
);